import { SimpleCarousel } from '@eventbrite/collection-carousel';
import {
    DestinationEvent,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { GenericLazyString } from '@eventbrite/i18n';
import { Typography } from '@eventbrite/marmalade';
import React from 'react';
import { useEnvContext } from '../../../../context';
import { EventCardContainer } from '../EventCardContainer';

import { LoadingCards } from '@eventbrite/eds-event-card';
import styles from './BentoCarousel.module.scss';

export interface BentoCarouselProps {
    title: string | GenericLazyString;
    titleURL?: string;
    lazyLoadCarouselElements?: boolean;
    events: Array<DestinationEvent | any>;
    isLoading?: boolean;
    activeCarousels?: number;
}

export interface BentoCarouselLoadingProps {
    title?: string | GenericLazyString;
    titleURL?: string;
    lazyLoadCarouselElements?: boolean;
    events?: Array<DestinationEvent | any>;
    isLoading?: boolean;
    activeCarousels?: number;
}

export interface CarouselTitleLinkProps {
    title?: string | GenericLazyString;
    titleURL?: string;
}

export const BentoCarousel: React.FC<
    BentoCarouselProps | BentoCarouselLoadingProps
> = (props) => {
    const {
        title,
        lazyLoadCarouselElements,
        events,
        titleURL,
        isLoading = false,
    } = props;
    const { env, user } = useEnvContext();

    const DEFAULT_CURRENCY_FORMAT = '¤#,##0.00';

    const BentoCarouselSlides = events?.map((event, i) => {
        return (
            <div key={'div' + event.id + i} style={{ display: 'block' }}>
                <EventCardContainer
                    isAuthenticated={user?.isAuthenticated ?? false}
                    eventData={transformDestinationEvent(event)}
                    key={'card' + event.id}
                    currencyFormat={
                        env?.currencyFormat ?? DEFAULT_CURRENCY_FORMAT
                    }
                    isMobile={env?.isMobile ?? false}
                    facebook_locale={env?.localeInfo.facebook_locale ?? 'en_US'}
                    serverUrl={env?.serverUrl ?? 'https://www.eventbrite.com'}
                    data-heap-id={`BentoCarousel-EventCard-${event.id}`}
                    // className prop specific to bentoCarousel.
                />
            </div>
        );
    });

    return (
        <>
            {isLoading ? (
                <div className={styles.bentoCarousel}>
                    <LoadingCards cardCount={4} style="grid" />
                </div>
            ) : (
                <div className={styles.bentoCarousel}>
                    <SimpleCarousel
                        data-heap-id={`BentoCarousel-${title}`}
                        slidesVisible={{ desktop: 4, tablet: 2 }}
                        headerTitle={
                            <div className={styles.headerSection}>
                                <div className={styles.titleContainer}>
                                    <Typography
                                        align="match-parent"
                                        as="h2"
                                        color="neutral-800"
                                        variant={
                                            env?.isMobile
                                                ? 'heading-sm'
                                                : 'heading-md'
                                        }
                                    >
                                        <CarouselTitleLink
                                            titleURL={titleURL}
                                            title={title}
                                        />
                                    </Typography>
                                </div>
                            </div>
                        }
                        lazilyRenderOffScreenElements={lazyLoadCarouselElements}
                    >
                        {BentoCarouselSlides}
                    </SimpleCarousel>
                </div>
            )}
        </>
    );
};

export const CarouselTitleLink: React.FC<CarouselTitleLinkProps> = (props) => {
    return (
        <>
            {props.titleURL ? (
                <a
                    className="eds-text-hs eds-text-color--grey-700"
                    href={props.titleURL}
                    data-testid="carousel-title-link"
                >
                    {props.title}
                </a>
            ) : (
                props.title
            )}
        </>
    );
};
